import { cn } from "@/utils";
import {
  ControlProps,
  optionIs,
  RankedTester,
  rankWith,
} from "@jsonforms/core";
import { withJsonFormsControlProps } from "@jsonforms/react";
import React, { useCallback, useRef } from "react";
import { EmailPreviewIframe } from "./HtmlEmailPreviewControl/EmailPreviewIframe";
import { EmailToolbar } from "./HtmlEmailPreviewControl/EmailToolbar";

const HtmlEmailPreviewControlComponent: React.FC<ControlProps> = ({
  data,
  enabled,
  handleChange,
  path,
  visible,
  id,
}) => {
  const iframeRef = useRef<HTMLIFrameElement | null>(null);

  const handleContentChange = useCallback(
    (newContent: string) => {
      handleChange(path, newContent);
    },
    [handleChange, path],
  );

  const readonly = !enabled;

  if (!visible) return null;

  return (
    <div className="mb-4" id={id}>
      <h2 className="text-sm font-medium mb-2">Email Body</h2>
      <div
        className={cn(
          "border rounded-lg overflow-hidden",
          "border-neutral-200 dark:border-neutral-800",
          "bg-n-00 dark:bg-n-10",
        )}
      >
        <EmailToolbar iframeRef={iframeRef} readonly={readonly} />
        <div className="relative">
          <EmailPreviewIframe
            ref={iframeRef}
            content={data || ""}
            onChange={handleContentChange}
            readonly={readonly}
          />
        </div>
      </div>
      {readonly && (
        <p className="text-xs text-muted-foreground mt-2">
          This email content is read-only.
        </p>
      )}
    </div>
  );
};

/**
 * Tester function that determines if this control should be used.
 * Matches when the UI schema has the option { control: "html_email_preview" }
 *
 * To use this control, add the following to your UI schema:
 * {
 *   "type": "Control",
 *   "scope": "#/properties/body",
 *   "options": {
 *     "control": "html_email_preview"
 *   }
 * }
 */
export const htmlEmailPreviewControlTester: RankedTester = rankWith(
  5,
  optionIs("control", "html_email_preview"),
);

export const HtmlEmailPreviewControl = withJsonFormsControlProps(
  HtmlEmailPreviewControlComponent,
);
